<!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- MCP connection management section (embedded in Profile) -->
<!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Removed AppLayout wrapper; embedded as Profile section under Integrations tab -->
<template>
  <div class="mcp-settings">

    <!-- ── Active connections ─────────────────────────────────────────── -->
    <div class="d-flex align-items-center justify-content-between mb-0">
      <div class="d-flex align-items-baseline" style="gap: 0.5rem">
        <span class="mcp-sub-heading">Active Connections</span>
        <span v-if="!connectionsLoading" class="text-muted" style="font-size:0.75rem">
          {{ connections.length }} / {{ maxConnections }} used
        </span>
      </div>
      <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT generation; paid users only -->
      <button
        v-if="isPaidUser"
        class="btn btn-sm btn-outline-primary"
        :disabled="!token || connections.length >= maxConnections"
        @click="openPatModal"
      >
        Generate Token
      </button>
    </div>
    <div v-if="connections.length >= maxConnections && !connectionsLoading" class="alert alert-warning small py-2 mt-2 mb-0">
      Connection limit reached. Revoke an existing connection to add a new one,
      or <a href="/profile#subscription">upgrade to Premium</a> for more connections.
    </div>

    <div v-if="connectionsLoading" class="text-muted mt-3">
      <span class="spinner-border spinner-border-sm mr-2"></span> Loading…
    </div>

    <div v-else-if="connections.length === 0" class="text-muted mt-3">
      No active connections.
    </div>

    <div v-else class="mt-3">
      <b-row class="mb-2">
        <b-col cols="3"><span class="detail-label">Client</span></b-col>
        <b-col><span class="detail-label">Scopes</span></b-col>
        <b-col cols="2"><span class="detail-label">Created</span></b-col>
        <b-col cols="2"><span class="detail-label">Last Used</span></b-col>
        <b-col cols="1"></b-col>
      </b-row>

      <b-row
        v-for="conn in connections"
        :key="conn.connectionId"
        class="mb-4 align-items-start"
      >
        <b-col cols="3">
          <strong class="small">{{ conn.clientName || conn.clientId || 'Unknown' }}</strong>
          <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Distinguish PAT connections from OAuth connections -->
          <span v-if="conn.pat" class="badge badge--audit ml-1" style="font-size:0.6rem">PAT</span>
          <div v-if="!conn.pat && conn.clientName && conn.clientName !== conn.clientId" class="text-muted" style="font-size:0.7rem; word-break:break-all">{{ conn.clientId }}</div>
        </b-col>
        <b-col>
          <span
            v-for="scope in conn.scopes"
            :key="scope"
            :class="['badge', 'scope-badge', scopeBadgeClass(scope)]"
          >{{ scope }}</span>
        </b-col>
        <b-col cols="2" class="text-muted small pt-1">{{ formatDate(conn.createdAt) }}</b-col>
        <b-col cols="2" class="text-muted small pt-1">
          <span v-if="conn.pat">Expires {{ formatDate(conn.expiresAt) }}</span>
          <span v-else>{{ formatDate(conn.lastUsedAt) || '—' }}</span>
        </b-col>
        <b-col cols="1" class="text-right">
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="revokingId === conn.connectionId"
            @click="confirmRevoke(conn)"
          >
            <span v-if="revokingId === conn.connectionId" class="spinner-border spinner-border-sm"></span>
            <span v-else>Revoke</span>
          </b-button>
        </b-col>
      </b-row>
    </div>

    <hr class="my-4" />

    <!-- ── Activity log ──────────────────────────────────────────────── -->
    <span class="mcp-sub-heading">Activity Log</span>
    <p class="section-description mt-1 mb-3">Recent AI operations on your tasks.</p>
    <McpAuditList v-if="token" :token="token" />

  </div>

  <!-- ── Add connection modal ───────────────────────────────────────────────── -->
  <div v-if="showConnectModal" class="modal-backdrop-custom" @click.self="showConnectModal = false">
    <div class="modal-dialog-custom card shadow-lg" @click.stop>
      <div class="card-header d-flex justify-content-between align-items-center">
        <strong>Add AI Connection</strong>
        <button class="btn-close" @click="showConnectModal = false"></button>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Connect an AI assistant to Principal Task. Choose the permissions you want to grant.
        </p>

        <div class="mb-3">
          <label class="form-label">Permissions</label>
          <div>
            <div class="form-check">
              <input id="scope-read" v-model="newConnScopes" class="form-check-input" type="checkbox" value="pt:read">
              <label class="form-check-label" for="scope-read">
                Read tasks, folders, and contexts
              </label>
            </div>
            <div class="form-check">
              <input id="scope-write" v-model="newConnScopes" class="form-check-input" type="checkbox" value="pt:write">
              <label class="form-check-label" for="scope-write">
                Create and update tasks
              </label>
            </div>
            <div class="form-check">
              <input id="scope-delete" v-model="newConnScopes" class="form-check-input" type="checkbox" value="pt:delete">
              <label class="form-check-label" for="scope-delete">
                Delete tasks (destructive — use with care)
              </label>
            </div>
            <div class="form-check">
              <input id="scope-audit" v-model="newConnScopes" class="form-check-input" type="checkbox" value="pt:audit:read">
              <label class="form-check-label" for="scope-audit">
                View this activity log from within AI assistants
              </label>
            </div>
          </div>
        </div>

        <div class="alert alert-info small py-2">
          <i class="fa fa-info-circle me-1"></i>
          After clicking <strong>Connect</strong>, your AI assistant will redirect you back here to
          complete authorization. You can revoke access at any time.
        </div>

        <div v-if="connectError" class="alert alert-danger small py-2">{{ connectError }}</div>

        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary" @click="showConnectModal = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="newConnScopes.length === 0 || connecting"
            @click="startConnect"
          >
            <span v-if="connecting" class="spinner-border spinner-border-sm me-1"></span>
            Connect
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ── Generate PAT modal ───────────────────────────────────────────────────── -->
  <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT generation modal; paid users only -->
  <div v-if="showPatModal" class="modal-backdrop-custom" @click.self="showPatModal = false">
    <div class="modal-dialog-custom card shadow-lg" @click.stop>
      <div class="card-header d-flex justify-content-between align-items-center">
        <strong>Generate Personal Access Token</strong>
        <button class="btn-close" @click="showPatModal = false"></button>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          A Personal Access Token lets headless AI clients (like OpenClaw) connect directly without a browser OAuth flow.
          The token is shown exactly once — save it somewhere safe before closing.
        </p>

        <div class="mb-3">
          <label class="form-label" for="pat-client-name">Token name</label>
          <input
            id="pat-client-name"
            v-model="patClientName"
            type="text"
            class="form-control form-control-sm"
            placeholder="e.g. OpenClaw, My Mac, Home Server"
            maxlength="80"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Permissions</label>
          <div>
            <div v-for="scope in SCOPE_DEFS" :key="scope.value" class="form-check">
              <input :id="`pat-scope-${scope.value}`" v-model="patScopes" class="form-check-input" type="checkbox" :value="scope.value">
              <label class="form-check-label" :for="`pat-scope-${scope.value}`">{{ scope.label }}</label>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label" for="pat-expiry">Expiration (days)</label>
          <input
            id="pat-expiry"
            v-model.number="patExpirationDays"
            type="number"
            class="form-control form-control-sm"
            min="1"
            max="365"
            style="max-width: 120px"
          />
          <div class="form-text text-muted" style="font-size:0.75rem">1–365 days. Default: 90.</div>
        </div>

        <div v-if="patError" class="alert alert-danger small py-2">{{ patError }}</div>

        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary" @click="showPatModal = false">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!patClientName.trim() || patScopes.length === 0 || patGenerating"
            @click="generatePat"
          >
            <span v-if="patGenerating" class="spinner-border spinner-border-sm me-1"></span>
            Generate
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ── PAT show-once reveal modal ────────────────────────────────────────────── -->
  <!-- 20260419 -- Copilot (Claude Sonnet 4.6) -- Token shown exactly once; user must paste to confirm before closing -->
  <div v-if="showPatRevealModal" class="modal-backdrop-custom">
    <div class="modal-dialog-custom card shadow-lg" @click.stop style="max-width: 560px">
      <div class="card-header">
        <strong>Your Personal Access Token</strong>
      </div>
      <div class="card-body">
        <div class="alert alert-warning small py-2 mb-3">
          <strong>This token will only be shown once.</strong>
          Copy it now and store it in a safe location (password manager, secrets vault, etc.).
          You will not be able to retrieve it again.
        </div>

        <label class="form-label" style="font-size:0.75rem; font-weight:600; text-transform:uppercase; letter-spacing:0.03em; color:#6c757d">Your token</label>
        <div class="pat-token-box d-flex align-items-center mb-3">
          <code class="pat-token-value flex-grow-1">{{ generatedToken }}</code>
          <button class="pat-copy-btn" :class="{ 'pat-copy-btn--copied': patCopied }" @click="copyPatToken" :title="patCopied ? 'Copied!' : 'Copy token'">
            <svg v-if="!patCopied" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="9" y="2" width="6" height="4" rx="1"/>
              <path d="M7 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>
        </div>

        <div class="mb-3">
          <label class="form-label" for="pat-verify">Paste your token here to confirm you've saved it</label>
          <input
            id="pat-verify"
            v-model="patVerifyInput"
            type="text"
            class="form-control form-control-sm"
            :class="patVerifyInput ? (patVerified ? 'is-valid' : 'is-invalid') : ''"
            placeholder="Paste token to confirm…"
            autocomplete="off"
            spellcheck="false"
          />
          <div v-if="patVerifyInput && !patVerified" class="invalid-feedback">Token doesn't match. Make sure you copied the full token.</div>
          <div v-if="patVerified" class="valid-feedback">Token confirmed.</div>
        </div>

        <div class="d-flex justify-content-end">
          <button class="btn btn-primary" :disabled="!patVerified" @click="closePatRevealModal">
            Done
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ── Revoke confirmation modal ──────────────────────────────────────────── -->
  <div v-if="revokeTarget" class="modal-backdrop-custom" @click.self="revokeTarget = null">
    <div class="modal-dialog-custom card shadow-lg" @click.stop>
      <div class="card-header">
        <strong>Revoke Connection</strong>
      </div>
      <div class="card-body">
        <p>
          Are you sure you want to revoke access for
          <strong>{{ revokeTarget.clientName || revokeTarget.clientId || 'this connection' }}</strong>?
          The AI assistant will no longer be able to access your tasks.
        </p>
        <div v-if="revokeError" class="alert alert-danger small py-2 mb-3">{{ revokeError }}</div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-secondary mr-2" @click="revokeTarget = null">Cancel</button>
          <button class="btn btn-danger" :disabled="!!revokingId" @click="revokeConnection(revokeTarget)">
            <span v-if="revokingId === revokeTarget.connectionId" class="spinner-border spinner-border-sm mr-1"></span>
            Revoke
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 20260419 -- Copilot (Claude Sonnet 4.6) -- McpSettings section logic (embedded in Profile)
import McpAuditList from '@/components/McpAuditList.vue'
import McpService from '@/services/McpService'
import genericOps from '@/mixins/genericOps'
import Logger from '@/utils/Logger'

const log = Logger.create('McpSettings')

// 20260419 -- Copilot (Claude Sonnet 4.6) -- Canonical scope list mirroring discovery.js MCP_SCOPES
const SCOPE_DEFS = [
  { value: 'pt:read',         label: 'Read tasks (search, retrieve, filter)' },
  { value: 'pt:structure:read', label: 'Read folders, contexts, priorities, and statuses' },
  { value: 'pt:write',        label: 'Create and update tasks' },
  { value: 'pt:complete',     label: 'Complete tasks' },
  { value: 'pt:delete',       label: 'Delete tasks (destructive — use with care)' },
  { value: 'pt:prefs:read',   label: 'Read preferences and default task options' },
  { value: 'pt:prefs:write',  label: 'Update preferences and default task options' },
  { value: 'pt:audit:read',   label: 'View activity log from within AI assistants' },
]

export default {
  name: 'McpSettings',

  mixins: [genericOps],

  components: { McpAuditList },

  data() {
    return {
      SCOPE_DEFS,
      token:              null,
      connections:        [],
      connectionsLoading: false,

      // Add connection modal
      showConnectModal: false,
      newConnScopes:    ['pt:read', 'pt:write'],
      connecting:       false,
      connectError:     '',

      // Revoke
      revokeTarget: null,
      revokingId:   null,
      revokeError:  '',

      // 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT generation
      showPatModal:       false,
      patClientName:      '',
      patScopes:          ['pt:read', 'pt:write'],
      patExpirationDays:  90,
      patGenerating:      false,
      patError:           '',

      // PAT show-once reveal
      showPatRevealModal: false,
      generatedToken:     '',
      patVerifyInput:     '',
      patCopied:          false,
    }
  },

  async created() {
    await this.preparePage()
  },

  computed: {
    // 20260419 -- Copilot (Claude Sonnet 4.6) -- Per-tier connection cap from env, same vars the backend uses
    maxConnections() {
      const tier = this.$store.getters.getUser?.subscriptionTier || 'free'
      return tier === 'paid'
        ? (parseInt(process.env.VUE_APP_ENV_MCP_MAX_CONNECTIONS_PAID) || 5)
        : (parseInt(process.env.VUE_APP_ENV_MCP_MAX_CONNECTIONS_FREE) || 2)
    },
    // 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT is paid-only; delegate to genericOps mixin
    isPaidUser() { return this.isPremiumUser() },
    patVerified() {
      return this.patVerifyInput.length > 0 && this.patVerifyInput === this.generatedToken
    },
  },

  methods: {
    async preparePage() {
      this.token = await this.$auth.getTokenSilently()
      this.$store.dispatch('setUserToken', this.token)
      await this.loadConnections()
    },

    async loadConnections() {
      this.connectionsLoading = true
      try {
        const res = await McpService.getConnections(this.token)
        this.connections = res.data?.data || []
      } catch (e) {
        log.error('loadConnections failed', e)
        this.connections = []
      } finally {
        this.connectionsLoading = false
      }
    },

    confirmRevoke(conn) {
      this.revokeTarget = conn
      this.revokeError  = ''
    },

    async revokeConnection(conn) {
      this.revokingId  = conn.connectionId
      this.revokeError = ''
      try {
        await McpService.revokeConnection(conn.connectionId, this.token)
        this.connections = this.connections.filter(c => c.connectionId !== conn.connectionId)
        this.revokeTarget = null
      } catch (e) {
        log.error('revokeConnection failed', e)
        this.revokeError = e.response?.data?.message || 'Failed to revoke connection. Please try again.'
      } finally {
        this.revokingId = null
      }
    },

    async startConnect() {
      this.connecting   = true
      this.connectError = ''
      try {
        // 20260419 -- Copilot (Claude Sonnet 4.6) -- Redirect to profile integrations tab after OAuth completes
        const redirectUri = `${window.location.origin}/profile#ai-connections`
        const res = await McpService.initiateOAuth(
          'claude.ai',
          this.newConnScopes.join(' '),
          redirectUri,
          this.token
        )
        const url = res.data?.data?.url
        if (url) {
          window.location.href = url
        } else {
          this.connectError = 'Could not generate authorization URL.'
        }
      } catch (e) {
        log.error('startConnect failed', e)
        this.connectError = e.response?.data?.error?.message || 'Failed to initiate connection.'
      } finally {
        this.connecting = false
      }
    },

    // 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT generation methods
    openPatModal() {
      this.patClientName     = ''
      this.patScopes         = ['pt:read', 'pt:write']
      this.patExpirationDays = 90
      this.patError          = ''
      this.showPatModal      = true
    },

    async generatePat() {
      this.patGenerating = true
      this.patError      = ''
      try {
        const res = await McpService.generatePat(this.token, {
          client_name:     this.patClientName.trim(),
          scopes:          this.patScopes,
          expiration_days: this.patExpirationDays,
        })
        const { token } = res.data?.data || {}
        this.showPatModal      = false
        this.generatedToken    = token
        this.patVerifyInput    = ''
        this.patCopied         = false
        this.showPatRevealModal = true
        await this.loadConnections()
      } catch (e) {
        log.error('generatePat failed', e)
        this.patError = e.response?.data?.message || 'Failed to generate token. Please try again.'
      } finally {
        this.patGenerating = false
      }
    },

    async copyPatToken() {
      try {
        await navigator.clipboard.writeText(this.generatedToken)
        this.patCopied = true
        setTimeout(() => { this.patCopied = false }, 2000)
      } catch (_) {}
    },

    closePatRevealModal() {
      this.showPatRevealModal = false
      this.generatedToken     = ''
      this.patVerifyInput     = ''
    },

    formatDate(ms) {
      if (!ms) return null
      return new Date(ms).toLocaleDateString()
    },

    scopeBadgeClass(scope) {
      if (['pt:read', 'pt:structure:read', 'pt:prefs:read'].includes(scope))  return 'badge--read'
      if (['pt:write', 'pt:complete', 'pt:prefs:write'].includes(scope))      return 'badge--write'
      if (scope === 'pt:delete')                                               return 'badge--delete'
      // pt:audit:read, openid, offline_access → gray
      return 'badge--audit'
    },
  },
}
</script>

<style scoped>
.mcp-sub-heading {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.detail-label {
  font-size: 0.625rem;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.scope-badge {
  display: inline-block;
  margin-right: 0.4rem;
  margin-bottom: 0.4rem;
  font-weight: 400;
}

/* Shared scope/tool color palette */
.badge--read   { background-color: #d1f5ec; color: #0e7052; border: 1px solid #a8e8d6; }
.badge--write  { background-color: #dde9ff; color: #1a4fbf; border: 1px solid #b6d0ff; }
.badge--delete { background-color: #ffe8d0; color: #c0520a; border: 1px solid #ffd0a0; }
.badge--audit  { background-color: #f0f2f4; color: #6c757d; border: 1px solid #dee2e6; }

/* PAT token display box */
.pat-token-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  gap: 0.5rem;
}

.pat-token-value {
  font-size: 0.7rem;
  word-break: break-all;
  color: #212529;
  flex: 1;
  min-width: 0;
}

.pat-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border: 1px solid #b6d0ff;
  background: transparent;
  color: #1a4fbf;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.pat-copy-btn:hover {
  background: #dde9ff;
}

.pat-copy-btn--copied {
  border-color: #a8e8d6;
  color: #0e7052;
}

.pat-copy-btn--copied:hover {
  background: #d1f5ec;
}

.detail-label {
  font-size: 0.625rem;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.scope-badge {
  display: inline-block;
  margin-right: 0.4rem;
  margin-bottom: 0.4rem;
  font-weight: 400;
}

/* Shared scope/tool color palette */
.badge--read   { background-color: #d1f5ec; color: #0e7052; border: 1px solid #a8e8d6; }
.badge--write  { background-color: #dde9ff; color: #1a4fbf; border: 1px solid #b6d0ff; }
.badge--delete { background-color: #ffe8d0; color: #c0520a; border: 1px solid #ffd0a0; }
.badge--audit  { background-color: #f0f2f4; color: #6c757d; border: 1px solid #dee2e6; }

/* Simple backdrop for modals without relying on Bootstrap's JS */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 480px;
  z-index: 1060;
}
</style>
